/* eslint-disable no-console */
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import React, { useCallback, useContext, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import useFetch from 'src/hooks/useFetch'
import {
  resetComponentsSettings,
  setComponentsSettingsLoading,
} from 'src/redux/actions/componentsSettingsActions'
import { RootState } from 'src/redux/store'
import useAuthenticationService from 'src/services/authentication'
import useFreezedTagItemsStore from 'src/stores/useFreezedTagItemsStore'
import { AccountInformations } from 'src/utils/account'
import { clearBackFromCognito } from 'src/utils/cognito'
import { API, CROSS_STORAGE_PLATFORM_URL } from 'src/utils/config'
import storageUtils from 'src/utils/storageUtils'
import { LOGIN_ROOT } from 'src/utils/urls'

const CROSS_STORAGE_CLEAR_TIMEOUT = 1000

type LoginFn = (email: string, password: string, remember: boolean) => Promise<any>
type LoginWithoutPromptFn = () => Promise<any>
type LoginWithCognitoFn = () => Promise<void>
type LoginWithKagemushaFn = (linkToken: string) => Promise<void>
type LogoutFn = (needToCallApi?: boolean) => Promise<any>
type ResetPasswordFn = (email: string) => Promise<any>
type ConfirmPasswordFn = (
  newPassword: string,
  passwordConfirm: string,
  confirmationToken?: string | null,
) => Promise<any>
type UpdatePasswordFn = (
  currentPassword: string,
  password: string,
  passwordConfirm: string,
) => Promise<any>
export type UpdateProfileFn = (profileId: string, cb?: () => void) => Promise<any>
type UpdateSettingsFn = (user: any) => Promise<any>
export type ConnectAsFn = (sellerId: string, cb?: () => void) => Promise<any>

export interface AccountCtx {
  informations: AccountInformations
  isLogged: boolean
  loadingSeller: boolean
  login: LoginFn
  loginWithoutPrompt: LoginWithoutPromptFn
  loginWithCognito: LoginWithCognitoFn
  loginWithKagemusha: LoginWithKagemushaFn
  logout: LogoutFn
  resetPassword: ResetPasswordFn
  confirmPassword: ConfirmPasswordFn
  updatePassword: UpdatePasswordFn
  updateProfile: UpdateProfileFn
  updateSettings: UpdateSettingsFn
  connectAs: ConnectAsFn
  getCurrentUser: () => Promise<AccountInformations>
  setLoadingSeller: (loading: boolean) => void
}

export const AccountContext = React.createContext<AccountCtx>({} as AccountCtx)

// Whether a stored redirect stays on the console. It comes from the
// ?redirect= of /login, which anyone can put in a link: a full URL or '//host'
// would send the user away, and 'javascript:' would run in the console, with
// the token in reach. Resolved the way the browser will navigate to it.
const isConsolePath = (path: string) => {
  try {
    return new URL(path, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

// Once the token is stored: back to the page the user was sent away from, or
// the dashboard. Shared by the password and the Cognito sign-in.
const redirectAfterLogin = () => {
  let redirectPath = localStorage.getItem('redirect')

  if (redirectPath && !isConsolePath(redirectPath)) {
    redirectPath = '/'
    localStorage.setItem('redirect', redirectPath)
  }
  const path =
    isEmpty(redirectPath) || isNull(redirectPath) || redirectPath === '/'
      ? '/report/dashboard'
      : redirectPath

  window.location.href = path
}

const AccountProvider = ({ children }) => {
  const { pathname, search } = useLocation()
  const [account, setAccount] = useState<AccountInformations | undefined>()
  const [isLogged, setIsLogged] = useState<boolean>(false)
  const [loadingSeller, setLoadingSeller] = useState(false)

  const setFreezedTagItems = useFreezedTagItemsStore(s => s.setFreezedTagItems)
  const {
    executeLogout,
    executeLogin,
    executeConnectAs,
    getCognitoConfig,
    exchangeCognitoSession,
    exchangeKagemushaLink,
  } = useAuthenticationService()

  const [, executeResetPassword] = useFetch(
    { url: API.USER_PASSWORD, method: 'POST' },
    { manual: true },
    false,
  )
  const [, executeConfirmPassword] = useFetch(
    { url: API.USER_CONFIRMATION, method: 'POST' },
    { manual: true },
    false,
  )
  const [, executeUpdatePassword] = useFetch(
    { url: API.USER_AUTH, method: 'PATCH' },
    { manual: true },
    false,
  )
  const [, executeUpdateProfile] = useFetch(
    { url: API.USER_PROFILES_SWITCH, method: 'PUT' },
    { manual: true },
  )
  const [, executeUpdateSettings] = useFetch(
    { url: API.USER_AUTH, method: 'PATCH' },
    { manual: true },
  )
  const [, executeGetCurrentUser] = useFetch<AccountInformations>(
    { url: API.USER_INFORMATIONS, method: 'GET' },
    { manual: true },
  )

  const getCurrentUser = useCallback(async () => {
    try {
      const { data } = await executeGetCurrentUser()

      setAccount(data)

      return data
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [executeGetCurrentUser])

  const login: LoginFn = useCallback(
    async (email, password, remember) => {
      try {
        const { token } = await executeLogin({ email, password, remember })

        if (!token) {
          throw new Error('No token found')
        }

        storageUtils.setAuthToken(token)
        redirectAfterLogin()
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeLogin],
  )

  // Back from Cognito, adomik_api holds a session for the user: trade it for
  // the same token the password login returns.
  const loginWithCognito: LoginWithCognitoFn = useCallback(async () => {
    const token = await exchangeCognitoSession()

    if (!token) {
      throw new Error('No token found')
    }

    storageUtils.setAuthToken(token)
    redirectAfterLogin()
  }, [exchangeCognitoSession])

  // From the intranet's impersonation link (SNOW-8234): same token, for the
  // kagemusha, traded for the one-time link instead of a session.
  const loginWithKagemusha: LoginWithKagemushaFn = useCallback(
    async (linkToken: string) => {
      const token = await exchangeKagemushaLink(linkToken)

      if (!token) {
        throw new Error('No token found')
      }

      storageUtils.setAuthToken(token)
      redirectAfterLogin()
    },
    [exchangeKagemushaLink],
  )

  const loginWithoutPrompt: LoginWithoutPromptFn = useCallback(async () => {
    try {
      const { data } = await executeGetCurrentUser()

      setAccount(data)
      setIsLogged(true)

      return data
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [executeGetCurrentUser])

  // A sign-out asked by the user wins over the automatic ones: once the token
  // is revoked, requests still in flight fail (401, or no token any more) and
  // call logout(false), whose state reset would mount the login page and send
  // the user back to Cognito before Cognito's own sign-out.
  const signingOutRef = useRef(false)

  const logout: LogoutFn = useCallback(
    async (needToCallApi = true) => {
      if (signingOutRef.current) return null
      if (needToCallApi) {
        signingOutRef.current = true
        clearBackFromCognito()
      }

      const redirect = encodeURIComponent(`${pathname}${search}`)
      const redirectionPath =
        !pathname.startsWith(LOGIN_ROOT) && redirect ? `/login/?redirect=${redirect}` : '/login'
      let destination = redirectionPath

      try {
        let logoutData: any = null
        if (needToCallApi) {
          // Ending our session is not enough: Cognito keeps its own, and the
          // login page would sign the user straight back in. Cognito's sign-out
          // returns to our login page. An expired token (needToCallApi false)
          // keeps it, so the next sign-in is silent. Asked before revoking the
          // token, so that the browser leaves right after.
          const cognito = await getCognitoConfig()
          if (cognito.enabled && cognito.logout_url) destination = cognito.logout_url

          const { data } = await executeLogout().catch(() => ({ data: null }))
          logoutData = data
        }

        storageUtils.logout()

        localStorage.setItem('redirect', redirectionPath)

        if (CROSS_STORAGE_PLATFORM_URL) {
          // Wait for cross-storage logout to be done
          await new Promise(resolve => setTimeout(resolve, CROSS_STORAGE_CLEAR_TIMEOUT))
        }
        return logoutData
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        // The page is left either way, so the state is not reset: that would
        // render the login page before the browser leaves, and its own redirect
        // to Cognito would race this navigation — replacing Cognito's sign-out,
        // or running a second sign-in alongside the reload.
        window.location.href = destination
      }
    },
    [executeLogout, getCognitoConfig, pathname, search],
  )

  const resetPassword: ResetPasswordFn = useCallback(
    async email => {
      try {
        const { data } = await executeResetPassword({ data: { user: { email } } })

        return data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeResetPassword],
  )

  const confirmPassword: ConfirmPasswordFn = useCallback(
    async (newPassword, passwordConfirm, confirmationToken) => {
      try {
        const userData: any = {
          password: newPassword,
          password_confirmation: passwordConfirm,
        }

        if (confirmationToken) {
          userData.confirmation_token = confirmationToken
        }

        const { data: account } = await executeConfirmPassword({
          data: {
            user: userData,
          },
        })

        setAccount(account as AccountInformations)

        return account
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeConfirmPassword],
  )

  const updatePassword: UpdatePasswordFn = useCallback(
    async (currentPassword, password, passwordConfirm) => {
      try {
        const { data } = await executeUpdatePassword({
          data: {
            user: {
              current_password: currentPassword,
              password,
              password_confirmation: passwordConfirm,
            },
          },
        })

        setAccount(data as AccountInformations)
        return data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeUpdatePassword],
  )

  const updateProfile: UpdateProfileFn = useCallback(
    async profileId => {
      try {
        const { data } = await executeUpdateProfile({
          data: {
            profile_id: profileId,
          },
        })
        setFreezedTagItems({})

        return data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeUpdateProfile],
  )

  const updateSettings: UpdateSettingsFn = useCallback(
    async user => {
      try {
        const { data } = await executeUpdateSettings({
          data: { user },
        })

        return data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeUpdateSettings],
  )

  const connectAs: ConnectAsFn = useCallback(
    async sellerId => {
      try {
        const { data } = await executeConnectAs(sellerId)
        setFreezedTagItems({})

        return data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [executeConnectAs, setFreezedTagItems],
  )

  const contextValue = {
    informations: account as AccountInformations,
    isLogged,
    loadingSeller,
    setLoadingSeller,
    login,
    loginWithoutPrompt,
    loginWithCognito,
    loginWithKagemusha,
    logout,
    getCurrentUser,
    resetPassword,
    confirmPassword,
    updatePassword,
    updateProfile,
    updateSettings,
    connectAs,
  }

  return <AccountContext.Provider value={contextValue}>{children}</AccountContext.Provider>
}

const mapStateToProps = (state: RootState) => ({
  settings: state.componentsSettings.settings,
  oldSettings: state.componentsSettings.oldSettings,
})
const mapDispatchToProps = {
  resetComponentsSettings,
  setComponentsSettingsLoading,
}

export const useAccountProviderCtx = () => useContext(AccountContext)

export default connect(mapStateToProps, mapDispatchToProps)(AccountProvider)
